import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import ArthaMultiSelect from "../../fields/ArthaMultiSelect";
import ArthaCancelButton from "../../fields/ArthaCancelButton";
import Select from "react-select";
import DatePicker from "react-datepicker";
import AddMember from "./AddMember";
import { ReactComponent as UserImage } from "../../Assets/mobileUser.svg";
import ArthaContinueButton from "../../fields/ArthaContinueButton";
import { handleDatePickerRawChange, dateFormatMaxLength } from "../../fields/datePickerInput";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from 'jspdf-autotable';
import "react-datepicker/dist/react-datepicker.css";
import "./dashboard.css";
import {
  fetchMemberApi,
  attendanceReport,
  fetchOrganizationUsers,
} from "../../actioncreators/actioncreators";
import {
  Search,
  Settings,
  Download,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useSuspension } from "../../contexts/SuspensionContext";

function GenerateAttendanceReport() {
  // Suspension check
  const { isSuspended } = useSuspension();
  const globalLocationFilter = useSelector((state) => state.selectedLocationFilter);
  const superAdminSelectedOrgId = useSelector((state) => state.superAdminSelectedOrgId);
  const superAdminSelectedOrgData = useSelector((state) => state.superAdminSelectedOrgData);
  const superAdminSelectedSubOrgId = superAdminSelectedOrgData?.type === 'sub_org' ? superAdminSelectedOrgData?.sub_org_id : null;
  const [isReportJustGenerated, setIsReportJustGenerated] = useState(false);
  const [isDownloadEnabled, setIsDownloadEnabled] = useState(false);
  const [hasGeneratedReport, setHasGeneratedReport] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState(null); // State to track selected member
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [selectedUsers, setSelectedUsers] = useState([]); // For multiple user selection
  const [users, setUsers] = useState([]); // Add this state for users list
  const [isFirstAccordionOpen, setIsFirstAccordionOpen] = useState(true);
  const [isSecondAccordionOpen, setIsSecondAccordionOpen] = useState(true); // Changed to true
  const [subAccordions, setSubAccordions] = useState({
    first: true,
    second: true,
    third: true,
    fourth: true,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [reportData, setReportData] = useState(null);

  const recordsPerPage = 20;
  const totalPages = reportData?.summaryData?.daily
    ? Math.ceil(reportData.summaryData.daily.length / recordsPerPage)
    : 0;

  const currentTableData = reportData?.summaryData?.daily
    ? reportData.summaryData.daily.slice(
        (currentPage - 1) * recordsPerPage,
        currentPage * recordsPerPage
      )
    : [];

  console.log("currentTableData", currentTableData);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetchOrganizationUsers(globalLocationFilter, superAdminSelectedOrgId, superAdminSelectedSubOrgId);
        if (response?.businessStatusCode === 2) {
          const userOptions =
            response.data?.map((user) => ({
              name: user.full_name,
              code: user.user_id,
            })) || [];
          console.log("userOptions", userOptions);
          setUsers(userOptions);
          // Reset selected users when location filter changes
          setSelectedUsers([]);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, [globalLocationFilter, superAdminSelectedOrgId, superAdminSelectedSubOrgId]);

 

  const handleGenerateReport = async () => {
    if (!selectedUsers || selectedUsers.length === 0) {
      toast.error("Please select at least one user");
      return;
    }
    const payload = {
      fromDate: fromDate.toISOString().split("T")[0],
      toDate: toDate.toISOString().split("T")[0],
      userId: selectedUsers.map(user => user.code),
    };
    // Super Admin: scope the report to the org selected in the top-bar filter.
    if (superAdminSelectedOrgId) payload.org_id = superAdminSelectedOrgId;
    if (superAdminSelectedSubOrgId) payload.sub_org_id = superAdminSelectedSubOrgId;
    try {
      const response = await attendanceReport(payload);
      if (response?.businessStatusCode === 2) {
        toast.success("Report downloaded successfully!");
        console.log("response", response);
        setReportData(() => response); // Fix: use function form to ensure latest value
        setIsReportJustGenerated(true);
        setIsDownloadEnabled(true);
        setHasGeneratedReport(true);
      }
    } catch (error) {
      console.error("Error generating report:", error);
      const serverMsg = error?.response?.data?.message;
      const status = error?.response?.status;
      if (serverMsg) {
        toast.error(serverMsg);
      } else if (status === 403) {
        toast.error("You do not have permission to generate a report for the selected users.");
      } else if (status === 401) {
        toast.error("Your session has expired. Please log in again.");
      } else {
        toast.error("Failed to generate report. Please try again.");
      }
    }
  };

  useEffect(() => {
    if (isReportJustGenerated) {
      setIsReportJustGenerated(false);
      // Only disable download if we haven't generated a report before
      if (!hasGeneratedReport) {
        setIsDownloadEnabled(false);
      }
    }
  }, [fromDate, toDate, selectedUsers]);

  const isFormValid = () => {
    return fromDate && toDate && selectedUsers && selectedUsers.length > 0;
  };

  const handleDownloadPDF = async () => {
    console.log('Download PDF button clicked');
    if (!selectedUsers || selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }
    const payload = {
      fromDate: fromDate.toISOString().split('T')[0],
      toDate: toDate.toISOString().split('T')[0],
      userId: selectedUsers.map(user => user.code),
    };
    // Super Admin: scope the report to the org selected in the top-bar filter.
    if (superAdminSelectedOrgId) payload.org_id = superAdminSelectedOrgId;
    if (superAdminSelectedSubOrgId) payload.sub_org_id = superAdminSelectedSubOrgId;
    let response;
    try {
      response = await attendanceReport(payload);
    } catch (error) {
      console.error("Error fetching report data for PDF:", error);
      const serverMsg = error?.response?.data?.message;
      const status = error?.response?.status;
      if (serverMsg) {
        toast.error(serverMsg);
      } else if (status === 403) {
        toast.error("You do not have permission to generate a report for the selected users.");
      } else if (status === 401) {
        toast.error("Your session has expired. Please log in again.");
      } else {
        toast.error("Failed to fetch report data. Please try again.");
      }
      return;
    }
    if (!response || !response.allUsersData || response.allUsersData.length === 0) {
      toast.error('No report data available for PDF download');
      return;
    }
    // Generate a single PDF for all users, each starting on a new page
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    response.allUsersData.forEach((user, idx) => {
      if (idx !== 0) doc.addPage();
      // Title
      doc.setFontSize(14);
      doc.setTextColor(34, 34, 34);
      doc.setFont('helvetica', 'bold');
      doc.text('Attendance Report', pageWidth / 2, 22, { align: 'center' });

      // Section Divider
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.7);
      doc.line(14, 27, pageWidth - 14, 27);

      // User Info
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Name:', 18, 38);
      doc.text('Onboarding Date:', 18, 46);
      doc.text('Role:', 18, 54);
      doc.text('Manager:', 18, 62);
      doc.text('Present Days:', 18, 70);
      doc.text('Absent Days:', 18, 78);
      doc.setFont('helvetica', 'normal');
      doc.text(`${user.fullName || '-'}`, 65, 38);
      doc.text(`${user.onboardedAt ? new Date(user.onboardedAt).toLocaleDateString('en-GB') : '-'}`, 65, 46);
      doc.text(`${user.roleName || '-'}`, 65, 54);
      doc.text(`${user.managerName || '-'}`, 65, 62);
      doc.text(`${user.presentDays ?? '-'}`, 65, 70);
      doc.text(`${user.absentDays ?? '-'}`, 65, 78);

      // Section Divider
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.line(14, 83, pageWidth - 14, 83);

      // Daily Attendance Table Title
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Daily Attendance', 18, 93);

      // Table
      const dailyRows = (user.daily || []).map((row) => [
        new Date(row.date).toLocaleDateString('en-GB'),
        row.day || '',
        row.presentStatus || '',
        row.workScheduleExpectedHr || row.workScheuleExpectedHr || '',
        row.actualWorkedHr || '',
        row.workScheuleStartTime || '',
        row.workScheuleEndTime || '',
        row.actualStartTime || '',
        row.actualEndTime || ''
      ]);
      autoTable(doc, {
        startY: 98,
        head: [[
          'Date', 'Day', 'Status', 'Expected Hr', 'Actual Worked Hr',
          'Schedule Start', 'Schedule End', 'Actual Start', 'Actual End'
        ]],
        body: dailyRows,
        theme: 'striped',
        headStyles: {
          fillColor: [44, 62, 80],
          textColor: [255, 255, 255],
          fontSize: 8,
          fontStyle: 'bold',
          halign: 'center',
          valign: 'middle',
        },
        bodyStyles: {
          fontSize: 7,
          textColor: [34, 34, 34],
          halign: 'center',
          valign: 'middle',
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 16 },
          2: { cellWidth: 18 },
          3: { cellWidth: 20 },
          4: { cellWidth: 24 },
          5: { cellWidth: 20 },
          6: { cellWidth: 20 },
          7: { cellWidth: 20 },
          8: { cellWidth: 20 },
        },
        margin: { left: 14, right: 14 },
        styles: {
          cellPadding: 2.5,
        },
        didDrawPage: (data) => {
          // Add a subtle footer line
          doc.setDrawColor(220, 220, 220);
          doc.setLineWidth(0.3);
          doc.line(14, doc.internal.pageSize.getHeight() - 18, pageWidth - 14, doc.internal.pageSize.getHeight() - 18);
        },
      });
    });
    // Footer for all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }
    doc.save('AttendanceReport_AllUsers.pdf');
  };

  return (
    <div className="row bg-white rounded shadow pt-3 px-3">
      <div className="col-12 col-md-12 d-flex justify-content-between p-0">
        <h1 className="fw-bold">Generate Attendance Report</h1>
        {/* Download PDF button removed as requested */}
      </div>

      <div className="row p-0 bg-white my-2 pb-1 border rounded d-flex m-0">
        {/* From Date Picker */}
        <div className="col-12 col-md-2 mb-2 pt-2">
          <label className="form-label">From Date</label>
          <DatePicker
            selected={fromDate}
            onChange={(date) => setFromDate(date)}
            onChangeRaw={handleDatePickerRawChange("dd/MM/yyyy")}
            className="form-control"
            dateFormat="dd/MM/yyyy"
            maxDate={toDate}
            maxLength={dateFormatMaxLength("dd/MM/yyyy")}
            withPortal
          />
        </div>

        {/* To Date Picker */}
        <div className="col-12 col-md-2 mb-2 pt-2">
          <label className="form-label">To Date</label>
          <DatePicker
            selected={toDate}
            onChange={(date) => setToDate(date)}
            onChangeRaw={handleDatePickerRawChange("dd/MM/yyyy")}
            className="form-control"
            dateFormat="dd/MM/yyyy"
            maxDate={new Date()}
            minDate={fromDate}
            maxLength={dateFormatMaxLength("dd/MM/yyyy")}
            withPortal
          />
        </div>

        {/* User Selection Dropdown */}
        <div className="col-12 col-md-5 mb-2 pt-2">
          <label className="form-label">Select User(s)</label>
          <ArthaMultiSelect
            options={
              [
                ...selectedUsers,
                ...users.filter(
                  (u) => !selectedUsers.some((sel) => sel.code === u.code)
                ),
              ]
            }
            value={selectedUsers}
            onChange={setSelectedUsers}
            placeholder={selectedUsers && selectedUsers.length > 0 ? `${selectedUsers.map(u => u.name).join(', ')}` : 'Select user(s)...'}
            display="chip"
          />
        </div>

        {/* Generate Report Button */}
        <div className="col-12 col-md-3 mb-2 d-flex align-items-end">
          <ArthaContinueButton
            onClick={isSuspended ? undefined : handleDownloadPDF}
            className="shadow-sm w-100"
            disabled={!isFormValid() || isSuspended}
            title={isSuspended ? "Download disabled - Organization is suspended" : ""}
            style={{
              backgroundColor: isSuspended ? '#ccc' : (isFormValid() ? '#ff7f0e' : '#ffe2d0'),
              color: isSuspended ? '#888' : (isFormValid() ? '#fff' : '#e0bfae'),
              border: 'none',
              borderRadius: '32px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              opacity: isSuspended ? 0.6 : (isFormValid() ? 1 : 0.7),
              cursor: (isSuspended || !isFormValid()) ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              fontSize: '16px',
              padding: '12px 0',
              transition: 'background 0.2s',
              letterSpacing: '0.5px',
            }}
          >
            <Download size={16} /> &nbsp; Download PDF
          </ArthaContinueButton>
        </div>
      </div>

      {reportData ? (
        <div>
          {/* Second Accordion - Table */}
          <div className="row mt-2 bg-white rounded border ">
            <div
              className="px-3 py-2 bg-gray-100 border-b cursor-pointer flex justify-between items-center rounded"
              onClick={() => setIsFirstAccordionOpen(!isFirstAccordionOpen)}
            >
              <h5 className="m-0 font-medium">Activity Report</h5>
              {isFirstAccordionOpen ? (
                <ChevronUp size={20} />
              ) : (
                <ChevronDown size={20} />
              )}
            </div>

            {isFirstAccordionOpen && (
              <div className="px-3">
                <div className="row overflow-x-auto">
                  <table className="min-w-full table-auto border-collapse">
                    <thead className="bg-gray-100">
                      <tr className="border-b-2 border-gray-300">
                        <th className="col-2 px-4 py-2 font-semibold text-gray-700">
                          Date
                        </th>
                        <th className="col-3 px-4 py-2 font-semibold text-gray-700">
                          Work Schedule Time
                        </th>
                        <th className="col-3 px-4 py-2 font-semibold text-gray-700">
                          Total Tracked Time
                        </th>
                        <th className="col-2 px-4 py-2 font-semibold text-gray-700">
                          Break Time
                        </th>
                        <th className="col-2 px-4 py-2 font-semibold text-gray-700">
                          Overtime
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {currentTableData &&
                        currentTableData.map((row, index) => (
                          <tr key={index} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-2 text-gray-800">
                              {new Date(row.date).toLocaleDateString("en-GB")}
                            </td>
                            <td className="px-4 py-2 text-gray-800">
                              {row.workScheduleTime || "0h 0m"}
                            </td>
                            <td className="px-4 py-2 text-gray-800">
                              {row.workedHours || "0h 0m"}
                            </td>
                            <td className="px-4 py-2 text-gray-800">
                              {row.breakHours || "0h 0m"}
                            </td>
                            <td className="px-4 py-2 text-gray-800">
                              {row.overtime || "0h 0m"}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                {/* {totalPages > 0 && (
                <div className="flex justify-center ">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`px-3 py-1 rounded ${
                        currentPage === i + 1
                          ? "bg-orange-1000 text-white"
                          : "bg-gray-200"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
              )} */}
              </div>
            )}
          </div>

          {/*  First Accordion - Sub Accordions */}
          <div className="row  bg-white my-3 rounded border ">
            <div
              className="px-3 py-2 bg-gray-100 border-b cursor-pointer flex justify-between items-center"
              onClick={() => setIsSecondAccordionOpen(!isSecondAccordionOpen)}
            >
              <h5 className="m-0 font-medium">Summary Totals</h5>
              {isSecondAccordionOpen ? (
                <ChevronUp size={20} />
              ) : (
                <ChevronDown size={20} />
              )}
            </div>

            {isSecondAccordionOpen && (
              <div className="">
                {/* Sub Accordions */}
                {[
                  {
                    key: "first",
                    title: "Base Times",
                    content: reportData && (
                      <div className="w-full rounded">
                        <div className="px-3 pb-2 border-b flex justify-between items-center">
                          <span>Total Work Schedule Time</span>
                          <span className="font-semibold">
                            {reportData.summaryData?.totalWorkScheduleTime ?? "0h 0m"}
                          </span>
                        </div>
                        <div className="px-3 py-2 border-b flex justify-between items-center">
                          <span>Total Break Time</span>
                          <span className="font-semibold">
                            {reportData.summaryData?.totalBreakTime ?? "0h 0m"}
                          </span>
                        </div>
                        <div className="px-3 py-2 border-b flex justify-between items-center">
                          <span>Total Allowed Break Time</span>
                          <span className="font-semibold">
                            {reportData.summaryData?.totalAllowedBreakTime ?? "0h 0m"}
                          </span>
                        </div>
                        <div className="px-3 pt-2 flex justify-between items-center">
                          <span>Total Over Time</span>
                          <span className="font-semibold">
                            {reportData.summaryData?.totalOverTime ?? "0h 0m"}
                          </span>
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: "second",
                    title: "Time With Actual Breaks",
                    content: reportData && (
                      <div className="w-full rounded">
                        <div className="px-3 pb-2 border-b flex justify-between items-center">
                          <span>Including Overtime</span>
                          <span className="font-semibold">
                            {
                              reportData.summaryData?.totalWorkedTimeWithActualBreakIncludeOT ?? "0h 0m"
                            }
                          </span>
                        </div>
                        <div className="px-3 pt-2 flex justify-between items-center">
                          <span>Excluding Overtime</span>
                          <span className="font-semibold">
                            {
                              reportData.summaryData?.totalWorkedTimeWithActualBreakExcludeOT ?? "0h 0m"
                            }
                          </span>
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: "third",
                    title: "Time With Allowed Breaks",
                    content: reportData && (
                      <div className="w-full rounded">
                        <div className="px-3 pb-2 border-b flex justify-between items-center">
                          <span>Including Overtime</span>
                          <span className="font-semibold">
                            {
                              reportData.summaryData?.totalWorkedTimeWithAllowedBreakIncludeOT ?? "0h 0m"
                            }
                          </span>
                        </div>
                        <div className="px-3 pt-2 flex justify-between items-center">
                          <span>Excluding Overtime</span>
                          <span className="font-semibold">
                            {
                              reportData.summaryData?.totalWorkedTimeWithAllowedBreakExcludeOT ?? "0h 0m"
                            }
                          </span>
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: "fourth",
                    title: "Application Usage",
                    content: reportData && (
                      <div className="w-full rounded mb-2">
                        <div className="px-3 pb-2 border-b flex justify-between items-center">
                          <span>Allowed Application Usage Time</span>
                          <span className="font-semibold">
                            {reportData.summaryData?.allowedAppUsageTime ?? "0h 0m"}
                          </span>
                        </div>
                        <div className="px-3 py-2 border-b flex justify-between items-center">
                          <span>Authorised Application Usage</span>
                          <span className="font-semibold">
                            {reportData.summaryData?.authorisedAppUsageTime ?? "0h 0m"}
                          </span>
                        </div>
                        <div className="px-3 pt-2 flex justify-between items-center">
                          <span>Unauthorised Application Usage</span>
                          <span className="font-semibold">
                            {reportData.summaryData?.unauthorisedAppUsageTime ?? "0h 0m"}
                          </span>
                        </div>
                      </div>
                    ),
                  },
                ].map((accordion) => (
                  <div
                    key={accordion.key}
                    className="border-b last:border-b-0  rounded"
                  >
                    <div
                      className="px-3 py-3 fw-semibold cursor-pointer flex justify-between items-center "
                      onClick={() =>
                        setSubAccordions({
                          ...subAccordions,
                          [accordion.key]: !subAccordions[accordion.key],
                        })
                      }
                    >
                      <h6 className="m-0">{accordion.title}</h6>
                      {subAccordions[accordion.key] ? (
                        <ChevronUp size={16} />
                      ) : (
                        <ChevronDown size={16} />
                      )}
                    </div>
                    {subAccordions[accordion.key] && (
                      <div className="px-3 py-2 bg-gray-50">
                        {accordion.content || (
                          <p>Content for {accordion.title}</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {reportData?.allUsersData && reportData.allUsersData.length > 0 && (
            <div className="mb-3 p-3 bg-light rounded border">
              <h5 className="mb-2">User Details</h5>
              <div className="row">
                <div className="col-md-4"><strong>Name:</strong> {reportData.allUsersData[0].fullName}</div>
                <div className="col-md-4"><strong>Onboarding Date:</strong> {reportData.allUsersData[0].onboardedAt ? new Date(reportData.allUsersData[0].onboardedAt).toLocaleDateString('en-GB') : '-'}</div>
                <div className="col-md-4"><strong>Manager:</strong> {reportData.allUsersData[0].managerName}</div>
              </div>
              <div className="row mt-2">
                <div className="col-md-4"><strong>Role:</strong> {reportData.allUsersData[0].roleName}</div>
                <div className="col-md-4"><strong>Present Days:</strong> {reportData.allUsersData[0].presentDays}</div>
                <div className="col-md-4"><strong>Absent Days:</strong> {reportData.allUsersData[0].absentDays}</div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="col-12 col-md-12 mx-auto px-5 py-3 mt-4">
          <UserImage
            alt="Image Not Found"
            className="w-50 h-50 mx-auto px-5 py-2"
          />
          <p className="fw-bold text-center mt-3">
            Kindly generate the report to view it here.
          </p>
        </div>
      )}
    </div>
  );
}

export default GenerateAttendanceReport;
